/**
 * useMachineSectionFilter.ts
 * BUG-2026-0121 #2 — soil/rock (LabSection) filter for the scenario machine grid.
 *
 * Consolidates the whole filter concern so Phase1MachineGrid stays lean:
 *   - persists the hidden-section set to localStorage,
 *   - derives the present sections (for the dropdown),
 *   - filters BOTH the machine rows AND their bookings/ghosts by section.
 *
 * Filtering bookings as well as rows is required because MachineScheduleGrid
 * auto-creates an extra row for any booking whose machine isn't in the row list;
 * without it, a hidden section's bars would reappear in their own re-made rows.
 */
import { useCallback, useMemo, useState } from 'react';
import type { LabSection, MachineBookingCalendar } from '../../../../types/schedule';
import type { MachineSummary } from '../../../../hooks/useScheduleData';

const STORAGE_KEY = 'belab.scenario.machineGrid.hiddenSections';

/** Read the persisted hidden-section set, tolerating absent/corrupt storage. */
function loadHiddenSections(): Set<LabSection> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed as LabSection[]) : new Set();
  } catch {
    return new Set();
  }
}

export interface MachineSectionFilterState {
  hiddenSections: Set<LabSection>;
  onHiddenChange: (next: Set<LabSection>) => void;
  presentSections: LabSection[];
  visibleMachines: MachineSummary[];
  visibleBookings: MachineBookingCalendar[];
  visibleGhostBookings: MachineBookingCalendar[];
  allHiddenByFilter: boolean;
}

export function useMachineSectionFilter(
  capableMachines: MachineSummary[],
  bookings: MachineBookingCalendar[],
  baselineBookings: MachineBookingCalendar[],
): MachineSectionFilterState {
  const [hiddenSections, setHiddenSections] = useState<Set<LabSection>>(loadHiddenSections);

  const onHiddenChange = useCallback((next: Set<LabSection>) => {
    setHiddenSections(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      // Persistence is best-effort; the filter still works for this session.
    }
  }, []);

  const presentSections = useMemo(
    () => [...new Set(
      capableMachines.map((m) => m.labSection).filter((s): s is LabSection => s !== null),
    )],
    [capableMachines],
  );

  const visibleMachines = useMemo(
    () => capableMachines.filter((m) => !(m.labSection && hiddenSections.has(m.labSection))),
    [capableMachines, hiddenSections],
  );

  const isHiddenSection = useCallback(
    (section: LabSection | null | undefined) => !!section && hiddenSections.has(section),
    [hiddenSections],
  );

  const visibleBookings = useMemo(
    () => bookings.filter((b) => !isHiddenSection(b.MachineLabSection)),
    [bookings, isHiddenSection],
  );
  const visibleGhostBookings = useMemo(
    () => baselineBookings.filter((b) => !isHiddenSection(b.MachineLabSection)),
    [baselineBookings, isHiddenSection],
  );

  const allHiddenByFilter = capableMachines.length > 0 && visibleMachines.length === 0;

  return {
    hiddenSections,
    onHiddenChange,
    presentSections,
    visibleMachines,
    visibleBookings,
    visibleGhostBookings,
    allHiddenByFilter,
  };
}
